import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { useAuth } from '../contexts/AuthContext';
import {
  adminConsentDocumentsApi,
  CONSENT_DOCUMENT_DATA_TYPE_OPTIONS,
  CONSENT_DOCUMENT_PURPOSE_OPTIONS,
  normalizeConsentDocumentMaster,
  RequestError,
  type ConsentDocumentAdmin,
  type ConsentDocumentSummary,
  type MarkdownValidationError,
  type MarkdownValidationErrorDetail,
} from '../api/client';
import {
  CONSENT_DOCUMENT_CATEGORIES,
  findCategoryByKey,
  findCategoryByValues,
  type ConsentDocumentCategory,
} from '../utils/consentDocumentCategories';
import { findForbiddenWords } from '../utils/forbiddenWords';
import { useFormDraft, useBeforeUnloadWarning } from '../hooks/useFormDraft';

/** API が返す raw 値（analysis / voice 等）を画面表示用の日本語ラベルに変換する。未登録は元の値を返す */
function translateValue(t: TFunction, namespace: string, value: string): string {
  const key = `admin.consentDocs.${namespace}.${value}`;
  const translated = t(key, { defaultValue: '' });
  return translated || value;
}

/**
 * BE が「同意本文に利用できない表現」として弾くパターンを事前検出する。
 * BE 仕様: マークダウン本文は HTML タグ・`javascript:` / `data:` スキームを禁止する
 */
const CONTENT_FORBIDDEN_PATTERNS: Array<{ regex: RegExp; hintKey: string }> = [
  { regex: /<\s*script\b/i, hintKey: 'admin.consentDocs.contentViolation.script' },
  { regex: /<\s*iframe\b/i, hintKey: 'admin.consentDocs.contentViolation.iframe' },
  { regex: /<\s*style\b/i, hintKey: 'admin.consentDocs.contentViolation.style' },
  { regex: /on\w+\s*=\s*["']/i, hintKey: 'admin.consentDocs.contentViolation.eventHandler' },
  { regex: /javascript\s*:/i, hintKey: 'admin.consentDocs.contentViolation.scriptUri' },
  { regex: /data\s*:\s*text\/html/i, hintKey: 'admin.consentDocs.contentViolation.dataUri' },
  // 汎用 HTML タグ（他の具体パターンに引っかからない場合のフォールバック）
  { regex: /<\s*\/?\s*[a-zA-Z][a-zA-Z0-9]*(\s[^<>]*)?>/, hintKey: 'admin.consentDocs.contentViolation.htmlTag' },
];

function findContentViolation(
  content: string
): { hintKey: string; snippet: string; line: number } | null {
  for (const { regex, hintKey } of CONTENT_FORBIDDEN_PATTERNS) {
    const m = regex.exec(content);
    if (m) {
      // 前後 10 文字を含む抜粋を作る
      const start = Math.max(0, m.index - 10);
      const end = Math.min(content.length, m.index + m[0].length + 10);
      // 検出箇所の行番号（1 始まり）
      const line = content.slice(0, m.index).split('\n').length;
      return { hintKey, snippet: content.slice(start, end), line };
    }
  }
  return null;
}

/**
 * マークダウン記法を知らない管理者でも使える書式ボタン付きエディタ用のアクション定義。
 * - linePrefix: 選択範囲内の各行の先頭に prefix を追加（見出し・箇条書き・引用など）
 * - wrap: 選択範囲を before / after で囲む（太字・リンクなど）
 * - insert: カーソル位置に text を挿入（水平線など）
 */
type ToolbarAction =
  | { type: 'linePrefix'; prefix: string }
  | { type: 'wrap'; before: string; after: string; placeholder: string }
  | { type: 'insert'; text: string };

interface ToolbarButton {
  key: string;
  labelKey: string;
  titleKey: string;
  action: ToolbarAction;
}

const TOOLBAR_BUTTONS: ToolbarButton[] = [
  {
    key: 'h2',
    labelKey: 'admin.consentDocs.toolbar.h2',
    titleKey: 'admin.consentDocs.toolbar.h2Title',
    action: { type: 'linePrefix', prefix: '## ' },
  },
  {
    key: 'h3',
    labelKey: 'admin.consentDocs.toolbar.h3',
    titleKey: 'admin.consentDocs.toolbar.h3Title',
    action: { type: 'linePrefix', prefix: '### ' },
  },
  {
    key: 'bold',
    labelKey: 'admin.consentDocs.toolbar.bold',
    titleKey: 'admin.consentDocs.toolbar.boldTitle',
    action: { type: 'wrap', before: '**', after: '**', placeholder: '強調したい文字' },
  },
  {
    key: 'ul',
    labelKey: 'admin.consentDocs.toolbar.ul',
    titleKey: 'admin.consentDocs.toolbar.ulTitle',
    action: { type: 'linePrefix', prefix: '- ' },
  },
  {
    key: 'ol',
    labelKey: 'admin.consentDocs.toolbar.ol',
    titleKey: 'admin.consentDocs.toolbar.olTitle',
    action: { type: 'linePrefix', prefix: '1. ' },
  },
  {
    key: 'link',
    labelKey: 'admin.consentDocs.toolbar.link',
    titleKey: 'admin.consentDocs.toolbar.linkTitle',
    action: { type: 'wrap', before: '[', after: '](https://)', placeholder: 'リンク名' },
  },
  {
    key: 'quote',
    labelKey: 'admin.consentDocs.toolbar.quote',
    titleKey: 'admin.consentDocs.toolbar.quoteTitle',
    action: { type: 'linePrefix', prefix: '> ' },
  },
  {
    key: 'hr',
    labelKey: 'admin.consentDocs.toolbar.hr',
    titleKey: 'admin.consentDocs.toolbar.hrTitle',
    action: { type: 'insert', text: '\n\n---\n\n' },
  },
];

interface ToolbarResult {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

function applyToolbarAction(textarea: HTMLTextAreaElement, action: ToolbarAction): ToolbarResult {
  const { selectionStart, selectionEnd, value } = textarea;
  if (action.type === 'linePrefix') {
    // 選択範囲を含む全行の先頭に prefix を挿入する
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const lineEndIdx = value.indexOf('\n', selectionEnd);
    const blockEnd = lineEndIdx === -1 ? value.length : lineEndIdx;
    const block = value.slice(lineStart, blockEnd);
    const modified = block
      .split('\n')
      .map((line) => action.prefix + line)
      .join('\n');
    return {
      value: value.slice(0, lineStart) + modified + value.slice(blockEnd),
      selectionStart: lineStart,
      selectionEnd: lineStart + modified.length,
    };
  }
  if (action.type === 'wrap') {
    const selected = value.slice(selectionStart, selectionEnd);
    const inner = selected || action.placeholder;
    const wrapped = action.before + inner + action.after;
    const newValue = value.slice(0, selectionStart) + wrapped + value.slice(selectionEnd);
    const innerStart = selectionStart + action.before.length;
    return {
      value: newValue,
      selectionStart: innerStart,
      selectionEnd: innerStart + inner.length,
    };
  }
  // insert
  const newValue = value.slice(0, selectionStart) + action.text + value.slice(selectionEnd);
  const caret = selectionStart + action.text.length;
  return { value: newValue, selectionStart: caret, selectionEnd: caret };
}

const SEMVER_REGEX = /^\d+\.\d+\.\d+$/;

type Mode = 'new' | 'edit' | 'view';

function isNewId(id: string | undefined): boolean {
  return !id || id === 'new';
}

function toDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

function toIsoUtc(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const ms = Date.parse(trimmed);
  if (Number.isNaN(ms)) return undefined;
  return new Date(ms).toISOString();
}

function formatDateTimeJst(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}

/** ADM-S06 同意文書管理（新規作成・編集・詳細） */
export default function AdminConsentDocumentEdit() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const isNew = isNewId(id);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [doc, setDoc] = useState<ConsentDocumentAdmin | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [purposeOptions, setPurposeOptions] = useState<string[]>([
    ...CONSENT_DOCUMENT_PURPOSE_OPTIONS,
  ]);
  const [dataTypeOptions, setDataTypeOptions] = useState<string[]>([
    ...CONSENT_DOCUMENT_DATA_TYPE_OPTIONS,
  ]);
  const [purpose, setPurpose] = useState<string>(CONSENT_DOCUMENT_PURPOSE_OPTIONS[0]);
  const [dataType, setDataType] = useState<string>(CONSENT_DOCUMENT_DATA_TYPE_OPTIONS[0]);
  /**
   * 管理者向けの簡易区分（利用規約 / プライバシーポリシー / 匿名データ利用 / 個人特定）。
   * 選択に応じて裏側の purpose / data_type / consent_mode を自動設定する
   */
  const [category, setCategory] = useState<ConsentDocumentCategory | ''>('');
  const [version, setVersion] = useState('1.0.0');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(() => toDateTimeLocal(new Date().toISOString()));
  // 招待受諾画面で表示するか（published / archived 状態でも切替可、BE 特例）
  const [showOnInvitePage, setShowOnInvitePage] = useState(false);
  const [showOnInviteSaving, setShowOnInviteSaving] = useState(false);
  const [showOnInviteError, setShowOnInviteError] = useState<string | null>(null);

  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [requireReconsent, setRequireReconsent] = useState(false);
  const [notifyUsers, setNotifyUsers] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  // 下書き削除（課題 No.48）。draft のみ実行でき、published / archived は BE が 409 で拒否する
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // 新規作成時の (purpose, data_type, version) 衝突チェック用に既存文書を読み込んでおく
  const [existingDocs, setExistingDocs] = useState<ConsentDocumentSummary[]>([]);

  const contentRef = useRef<HTMLTextAreaElement | null>(null);

  // ドラフト保護（2026-05-31）: refresh 失敗 / 別タブからのログアウト伝搬 / ブラウザリロード等で
  // 未保存の Markdown 編集内容が消失するのを防ぐ。published / archived 状態の編集はできないため
  // 新規作成と draft 編集に限定。
  const draftKey = `admin/consent-edit/${isNew ? 'new' : id ?? 'unknown'}`;
  const draftValue = useMemo(
    () => ({ purpose, dataType, category, version, title, content, effectiveFrom, showOnInvitePage }),
    [purpose, dataType, category, version, title, content, effectiveFrom, showOnInvitePage]
  );
  const { clearDraft } = useFormDraft(draftKey, draftValue, (d) => {
    if (typeof d?.purpose === 'string') setPurpose(d.purpose);
    if (typeof d?.dataType === 'string') setDataType(d.dataType);
    if (typeof d?.category === 'string') setCategory(d.category as ConsentDocumentCategory);
    if (typeof d?.version === 'string') setVersion(d.version);
    if (typeof d?.title === 'string') setTitle(d.title);
    if (typeof d?.content === 'string') setContent(d.content);
    if (typeof d?.effectiveFrom === 'string') setEffectiveFrom(d.effectiveFrom);
    if (typeof d?.showOnInvitePage === 'boolean') setShowOnInvitePage(d.showOnInvitePage);
  }, { enabled: !loading && (isNew || doc?.status === 'draft') });
  const isDirty = Boolean(title.trim() || content.trim());
  useBeforeUnloadWarning(isDirty && !saving && (isNew || doc?.status === 'draft'));

  /**
   * 区分を選ぶと裏側の BE 3 フィールドを自動設定。
   * 新規作成時はバージョンのメジャーも推奨値で上書きする（衝突回避のため）
   */
  const handleCategoryChange = (key: ConsentDocumentCategory | '') => {
    setCategory(key);
    if (!key) return;
    const def = findCategoryByKey(key);
    if (!def) return;
    setPurpose(def.values.purpose);
    setDataType(def.values.data_type);
    if (isNew) {
      setVersion(`${def.defaultMajorVersion}.0.0`);
    }
  };

  /** 書式ボタンクリック時の処理。content を更新し、次フレームでカーソル位置を復元する */
  const handleToolbar = (action: ToolbarAction) => {
    const textarea = contentRef.current;
    if (!textarea) return;
    const result = applyToolbarAction(textarea, action);
    setContent(result.value);
    // setState 後の再レンダリングを待って selection を復元する
    requestAnimationFrame(() => {
      const ta = contentRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(result.selectionStart, result.selectionEnd);
    });
  };

  const mode: Mode = useMemo(() => {
    if (isNew) return 'new';
    if (doc?.status === 'draft') return 'edit';
    return 'view';
  }, [isNew, doc]);

  /** 現在入力中の (purpose, data_type, version) が既存文書と衝突していればその要約を返す */
  const collisionDoc = useMemo<ConsentDocumentSummary | null>(() => {
    if (!isNew) return null;
    const v = version.trim();
    if (!SEMVER_REGEX.test(v)) return null;
    return (
      existingDocs.find(
        (d) => d.purpose === purpose && d.data_type === dataType && d.version === v
      ) ?? null
    );
  }, [isNew, purpose, dataType, version, existingDocs]);

  /** 同じ (purpose, data_type) で衝突しないバージョン候補（パッチ／マイナー／メジャー） */
  const versionSuggestions = useMemo<string[]>(() => {
    if (!isNew) return [];
    return computeVersionSuggestions(existingDocs, purpose, dataType);
  }, [isNew, existingDocs, purpose, dataType]);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    adminConsentDocumentsApi
      .getMaster(token)
      .then((res) => {
        const master = normalizeConsentDocumentMaster(res);
        if (master.purposes.length > 0) setPurposeOptions(master.purposes);
        if (master.data_types.length > 0) setDataTypeOptions(master.data_types);
      })
      .catch(() => {
        /* マスタ取得に失敗した場合は FE 側の仮マスタ（初期値）を継続使用 */
      });
  }, [getToken]);

  // 新規作成時のみ、衝突チェック用に既存文書（draft / published / archived 全て）を読み込む
  useEffect(() => {
    if (!isNew) return;
    const token = getToken();
    if (!token) return;
    adminConsentDocumentsApi
      .list(token, { status: ['draft', 'published', 'archived'], include_archived: true })
      .then((res) => setExistingDocs(res.data ?? []))
      .catch(() => {
        /* 既存文書一覧の取得に失敗しても画面は出す（衝突チェックがオフになるだけ） */
      });
  }, [isNew, getToken]);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    const token = getToken();
    if (!token || !id) return;
    setLoadError(null);
    setLoading(true);
    adminConsentDocumentsApi
      .get(id, token)
      .then((data) => {
        setDoc(data);
        setPurpose(data.purpose);
        setDataType(data.data_type);
        setVersion(data.version);
        setTitle(data.title);
        setContent(data.content ?? '');
        setEffectiveFrom(toDateTimeLocal(data.effective_from));
        setShowOnInvitePage(Boolean(data.show_on_invite_page));
        // BE フィールドから簡易区分を逆引きしてセット
        const detected = findCategoryByValues({
          purpose: data.purpose,
          data_type: data.data_type,
          consent_mode: data.consent_mode,
          version: data.version,
        });
        if (detected) setCategory(detected.key);
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : t('dashboard.errorLoad')))
      .finally(() => setLoading(false));
  }, [id, isNew, getToken, t]);

  const validate = (): string | null => {
    if (isNew && !category) return t('admin.consentDocs.error.categoryRequired');
    if (!SEMVER_REGEX.test(version.trim())) return t('admin.consentDocs.error.versionInvalid');
    if (!title.trim()) return t('admin.consentDocs.error.titleRequired');
    if (!content.trim()) return t('admin.consentDocs.error.contentRequired');
    if (!toIsoUtc(effectiveFrom)) return t('admin.consentDocs.error.effectiveFromInvalid');
    const violation = findContentViolation(content);
    if (violation) {
      return t('admin.consentDocs.error.contentViolationWithLine', {
        hint: t(violation.hintKey),
        snippet: violation.snippet,
        line: violation.line,
      });
    }
    // 送信前に既知の衝突をガード
    if (collisionDoc) {
      const suggestList = versionSuggestions.join(' / ');
      return t('admin.consentDocs.error.collisionPreSubmit', {
        version: collisionDoc.version,
        suggestions: suggestList,
      });
    }
    return null;
  };

  /**
   * 422 + MARKDOWN_VALIDATION_FAILED 応答の details から errors[] を取り出す。
   * BE 仕様: `{ error_code, details: { errors: [{ line, column, rule, message, snippet? }] } }`
   */
  const extractMarkdownValidationErrors = (
    err: unknown
  ): MarkdownValidationError[] | null => {
    if (!(err instanceof RequestError)) return null;
    if (err.status !== 422) return null;
    if (err.errorCode !== 'MARKDOWN_VALIDATION_FAILED') return null;
    const details = err.details as MarkdownValidationErrorDetail | undefined;
    if (!details || !Array.isArray(details.errors)) return null;
    const filtered: MarkdownValidationError[] = [];
    for (const item of details.errors) {
      if (!item || typeof item !== 'object') continue;
      const row = item as unknown as Record<string, unknown>;
      const line = typeof row.line === 'number' ? row.line : Number(row.line);
      const column = typeof row.column === 'number' ? row.column : Number(row.column);
      const rule = typeof row.rule === 'string' ? row.rule : '';
      const message = typeof row.message === 'string' ? row.message : '';
      if (!Number.isFinite(line) || !Number.isFinite(column) || !rule || !message) {
        continue;
      }
      const snippet = typeof row.snippet === 'string' ? row.snippet : null;
      filtered.push({ line, column, rule, message, snippet });
    }
    return filtered.length > 0 ? filtered : null;
  };

  /** 400/422 応答時、既知のパターンを検出して FE 側でヒントを付記する */
  const buildSaveErrorMessage = (err: unknown): string => {
    const base = err instanceof Error ? err.message : t('common.error');

    // 1. 構造化 Markdown バリデーションエラー（2026-05-30〜）を優先処理
    const mdErrors = extractMarkdownValidationErrors(err);
    if (mdErrors) {
      const header = t('admin.consentDocs.error.markdownValidationHeader', {
        count: mdErrors.length,
      });
      const lines = mdErrors.map((e) =>
        t('admin.consentDocs.error.markdownValidationLine', {
          line: e.line,
          column: e.column,
          rule: e.rule,
          message: e.message,
        })
      );
      return `${header}\n${lines.join('\n')}`;
    }

    if (base.includes('利用できない表現')) {
      const violation = findContentViolation(content);
      if (violation) {
        return `${base}\n${t('admin.consentDocs.error.contentViolationWithLine', {
          hint: t(violation.hintKey),
          snippet: violation.snippet,
          line: violation.line,
        })}`;
      }
      return `${base}\n${t('admin.consentDocs.contentViolation.generic')}`;
    }
    // BE が「同一の purpose / data_type / version の文書が既に存在します」を返すケース
    if (base.includes('同一の') || base.includes('既に存在')) {
      const suggestList = versionSuggestions.join(' / ');
      if (suggestList) {
        return `${base}\n${t('admin.consentDocs.collision.serverHint', { suggestions: suggestList })}`;
      }
    }
    return base;
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token) return;
    const validationError = validate();
    if (validationError) {
      setSaveError(validationError);
      return;
    }
    const effectiveFromIso = toIsoUtc(effectiveFrom);
    if (!effectiveFromIso) return;
    setSaveError(null);
    setSaving(true);
    if (isNew) {
      adminConsentDocumentsApi
        .create(
          {
            purpose,
            data_type: dataType,
            version: version.trim(),
            title: title.trim(),
            content,
            effective_from: effectiveFromIso,
            show_on_invite_page: showOnInvitePage,
          },
          token
        )
        .then((created) => {
          clearDraft();
          navigate(`/admin/consent/${created.id}`, { replace: true });
        })
        .catch((err) => {
          setSaveError(buildSaveErrorMessage(err));
          setSaving(false);
        });
    } else if (id && doc?.status === 'draft') {
      adminConsentDocumentsApi
        .update(
          id,
          {
            title: title.trim(),
            content,
            version: version.trim(),
            effective_from: effectiveFromIso,
            show_on_invite_page: showOnInvitePage,
          },
          token
        )
        .then((updated) => {
          setDoc(updated);
          clearDraft();
          setSaving(false);
        })
        .catch((err) => {
          setSaveError(buildSaveErrorMessage(err));
          setSaving(false);
        });
    }
  };

  /**
   * 招待表示フラグのみを即時切替える（published / archived でも使える BE 特例）。
   * 編集画面のメイン保存とは独立して、チェックボックス変更時にその場で PUT する
   */
  const handleToggleShowOnInvite = (next: boolean) => {
    setShowOnInvitePage(next);
    if (!id || isNew || !doc) return;
    const token = getToken();
    if (!token) return;
    setShowOnInviteError(null);
    setShowOnInviteSaving(true);
    adminConsentDocumentsApi
      .update(id, { show_on_invite_page: next }, token)
      .then((updated) => setDoc(updated))
      .catch((err) => {
        setShowOnInviteError(err instanceof Error ? err.message : t('common.error'));
        // 失敗したら元に戻す
        setShowOnInvitePage(!next);
      })
      .finally(() => setShowOnInviteSaving(false));
  };

  const handlePublish = () => {
    const token = getToken();
    if (!token || !id || !doc) return;
    setPublishError(null);
    setPublishing(true);
    adminConsentDocumentsApi
      .publish(id, { require_reconsent: requireReconsent, notify_users: notifyUsers }, token)
      .then((updated) => {
        setDoc(updated);
        setPublishDialogOpen(false);
      })
      .catch((err) => setPublishError(err instanceof Error ? err.message : t('common.error')))
      .finally(() => setPublishing(false));
  };

  const handleArchive = () => {
    const token = getToken();
    if (!token || !id || !doc) return;
    setArchiveError(null);
    setArchiving(true);
    adminConsentDocumentsApi
      .archive(id, { reason: archiveReason.trim() || undefined }, token)
      .then((updated) => {
        setDoc(updated);
        setArchiveDialogOpen(false);
      })
      .catch((err) => setArchiveError(err instanceof Error ? err.message : t('common.error')))
      .finally(() => setArchiving(false));
  };

  /**
   * 下書きを削除する（課題 No.48）。成功したら一覧へ戻る。
   * 下書きの復元はできないため、確認ダイアログを経由してから呼ぶ。
   * BE 未反映の間は 404 / 405 が返るため、その場合は専用の文言を出す。
   */
  const handleDelete = () => {
    const token = getToken();
    if (!token || !id || !doc) return;
    setDeleteError(null);
    setDeleting(true);
    adminConsentDocumentsApi
      .delete(id, token)
      .then(() => {
        clearDraft();
        navigate('/admin/consent');
      })
      .catch((err) => {
        if (err instanceof RequestError) {
          if (err.status === 409) {
            // published / archived を削除しようとした（BE: INVALID_STATE_TRANSITION）
            setDeleteError(t('admin.consentDocs.deleteDialog.errorInvalidState'));
            return;
          }
          if (err.status === 404 || err.status === 405) {
            setDeleteError(t('admin.consentDocs.deleteDialog.errorNotAvailable'));
            return;
          }
        }
        setDeleteError(err instanceof Error ? err.message : t('common.error'));
      })
      .finally(() => setDeleting(false));
  };

  const handleNewVersion = () => {
    if (!doc) return;
    const next = bumpMinor(doc.version);
    navigate('/admin/consent/new', {
      state: {
        prefill: {
          purpose: doc.purpose,
          data_type: doc.data_type,
          version: next,
          title: doc.title,
          content: doc.content,
          parent_id: doc.id,
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="page admin-consent-docs-page">
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page admin-consent-docs-page">
        <div className="admin-error" role="alert">
          <p>{loadError}</p>
        </div>
        <p className="admin-back">
          <Link to="/admin/consent">{t('common.back')} → {t('admin.consentDocs.title')}</Link>
        </p>
      </div>
    );
  }

  const headingKey =
    mode === 'new'
      ? 'admin.consentDocs.headingNew'
      : mode === 'edit'
      ? 'admin.consentDocs.headingEdit'
      : 'admin.consentDocs.headingView';

  const readOnly = mode === 'view';

  return (
    <div className="page admin-consent-docs-page">
      <h1>{t(headingKey)}</h1>
      {doc && (
        <p className="admin-consent-docs-meta">
          {t('admin.consentDocs.docId')}: <code>{doc.id}</code> / {t('admin.consentDocs.col.status')}:{' '}
          <span className={`admin-badge admin-consent-docs-status-${doc.status}`}>
            {t(`admin.consentDocs.status.${doc.status}`)}
          </span>
        </p>
      )}

      <form onSubmit={handleSave} className="admin-card admin-form admin-consent-docs-form">
        <div className="admin-form-group">
          <label htmlFor="admin-consentDocs-field-category">{t('admin.consentDocs.field.category')}</label>
          <select id="admin-consentDocs-field-category"
            value={category}
            onChange={(e) => handleCategoryChange(e.target.value as ConsentDocumentCategory | '')}
            disabled={readOnly || !isNew}
            required={isNew}
          >
            <option value="">{t('admin.consentDocs.category.placeholder')}</option>
            {CONSENT_DOCUMENT_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
          {(() => {
            const def = findCategoryByKey(category || null);
            if (def) {
              return <p className="admin-form-hint">{def.description}</p>;
            }
            return <p className="admin-form-hint">{t('admin.consentDocs.hint.category')}</p>;
          })()}
          {!isNew && (
            <p className="admin-form-hint">{t('admin.consentDocs.hint.categoryLocked')}</p>
          )}
        </div>

        <details className="admin-consent-docs-advanced">
          <summary>{t('admin.consentDocs.advanced.toggle')}</summary>
          <p className="admin-form-hint">{t('admin.consentDocs.advanced.hint')}</p>
          <div className="admin-consent-docs-form-row">
            <div className="admin-form-group">
              <label htmlFor="admin-consentDocs-field-purpose">{t('admin.consentDocs.field.purpose')}</label>
              <select id="admin-consentDocs-field-purpose"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                disabled={readOnly || !isNew}
              >
                {(purposeOptions.includes(purpose) ? purposeOptions : [purpose, ...purposeOptions]).map((p) => (
                  <option key={p} value={p}>{translateValue(t, 'purposeValue', p)}</option>
                ))}
              </select>
            </div>
            <div className="admin-form-group">
              <label htmlFor="admin-consentDocs-field-dataType">{t('admin.consentDocs.field.dataType')}</label>
              <select id="admin-consentDocs-field-dataType"
                value={dataType}
                onChange={(e) => setDataType(e.target.value)}
                disabled={readOnly || !isNew}
              >
                {(dataTypeOptions.includes(dataType) ? dataTypeOptions : [dataType, ...dataTypeOptions]).map((d) => (
                  <option key={d} value={d}>{translateValue(t, 'dataTypeValue', d)}</option>
                ))}
              </select>
            </div>
          </div>
        </details>

        <div className="admin-consent-docs-form-row">
          <div className="admin-form-group">
            <label htmlFor="admin-consentDocs-field-version">{t('admin.consentDocs.field.version')}</label>
            <input id="admin-consentDocs-field-version"
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="1.0.0"
              disabled={readOnly}
              required
              aria-invalid={collisionDoc != null}
            />
            <p className="admin-form-hint">{t('admin.consentDocs.hint.semver')}</p>
            {collisionDoc && (() => {
              // BE が返す *_label_ja を優先し、未同梱の場合は translateValue でフォールバック
              const cd = collisionDoc as ConsentDocumentSummary & {
                purpose_label_ja?: string;
                data_type_label_ja?: string;
              };
              return (
              <div className="admin-form-warning" role="alert">
                <p>
                  {t('admin.consentDocs.collision.warning', {
                    purpose: cd.purpose_label_ja || translateValue(t, 'purposeValue', collisionDoc.purpose),
                    dataType: cd.data_type_label_ja || translateValue(t, 'dataTypeValue', collisionDoc.data_type),
                    version: collisionDoc.version,
                    status: t(`admin.consentDocs.status.${collisionDoc.status}`),
                  })}
                </p>
                {versionSuggestions.length > 0 && (
                  <p className="admin-consent-docs-collision-suggest">
                    <span>{t('admin.consentDocs.collision.suggest')}</span>
                    {versionSuggestions.map((v) => (
                      <button
                        key={v}
                        type="button"
                        className="admin-consent-docs-collision-btn"
                        onClick={() => setVersion(v)}
                        title={t('admin.consentDocs.collision.applyTitle', { version: v })}
                      >
                        {v}
                      </button>
                    ))}
                  </p>
                )}
              </div>
              );
            })()}
          </div>
        </div>

        <div className="admin-form-group">
          <label htmlFor="admin-consentDocs-field-title">{t('admin.consentDocs.field.title')}</label>
          <input id="admin-consentDocs-field-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            disabled={readOnly}
            required
          />
        </div>

        <div className="admin-form-group">
          <label htmlFor="admin-consentDocs-field-effectiveFrom">{t('admin.consentDocs.field.effectiveFrom')}</label>
          <input id="admin-consentDocs-field-effectiveFrom"
            type="datetime-local"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            disabled={readOnly}
            required
          />
          <p className="admin-form-hint">{t('admin.consentDocs.hint.effectiveFrom')}</p>
        </div>

        <div className="admin-form-group admin-consent-docs-invite-toggle">
          <label className="admin-consent-docs-invite-toggle-label">
            <input
              type="checkbox"
              checked={showOnInvitePage}
              onChange={(e) => handleToggleShowOnInvite(e.target.checked)}
              disabled={showOnInviteSaving}
            />
            <span>{t('admin.consentDocs.field.showOnInvite')}</span>
            {showOnInviteSaving && (
              <span className="admin-form-hint">（{t('common.loading')}）</span>
            )}
          </label>
          <p className="admin-form-hint">{t('admin.consentDocs.hint.showOnInvite')}</p>
          {showOnInviteError && <p className="admin-form-error">{showOnInviteError}</p>}
        </div>

        <div className="admin-form-group">
          <label htmlFor="admin-consentDocs-field-content">{t('admin.consentDocs.field.content')}</label>
          {!readOnly && (
            <div
              className="admin-consent-docs-toolbar"
              role="toolbar"
              aria-label={t('admin.consentDocs.toolbar.label')}
            >
              {TOOLBAR_BUTTONS.map((btn) => (
                <button
                  key={btn.key}
                  type="button"
                  className="admin-consent-docs-toolbar-btn"
                  title={t(btn.titleKey)}
                  aria-label={t(btn.titleKey)}
                  onMouseDown={(e) => {
                    // テキストエリアのフォーカスを失わないよう、クリック前の mousedown で止める
                    e.preventDefault();
                  }}
                  onClick={() => handleToolbar(btn.action)}
                >
                  {t(btn.labelKey)}
                </button>
              ))}
            </div>
          )}
          <div className="admin-consent-docs-editor">
            <textarea id="admin-consentDocs-field-content"
              ref={contentRef}
              className="admin-textarea admin-consent-docs-textarea"
              rows={16}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={readOnly}
              required
              placeholder={t('admin.consentDocs.field.contentPlaceholder')}
            />
            <div className="admin-consent-docs-preview" aria-label={t('admin.consentDocs.previewLabel')}>
              <div className="admin-consent-docs-preview-heading">
                {t('admin.consentDocs.previewLabel')}
              </div>
              <div className="admin-consent-docs-preview-body">
                {content.trim() ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                    {content}
                  </ReactMarkdown>
                ) : (
                  <p className="admin-consent-docs-preview-empty">
                    {t('admin.consentDocs.previewEmpty')}
                  </p>
                )}
              </div>
            </div>
          </div>
          <p className="admin-form-hint">{t('admin.consentDocs.hint.toolbar')}</p>
          {(() => {
            const hits = findForbiddenWords(content);
            if (hits.length === 0) return null;
            return (
              <div className="admin-form-warning" role="status">
                <p>
                  <strong>{t('admin.consentDocs.forbiddenWordsWarning.title')}</strong>
                </p>
                <ul className="admin-consent-docs-forbidden-list">
                  {hits.map((h) => (
                    <li key={h.word}>
                      「{h.word}」 — {t('admin.consentDocs.forbiddenWordsWarning.snippet', { snippet: h.snippet })}
                    </li>
                  ))}
                </ul>
                <p className="admin-form-hint">
                  {t('admin.consentDocs.forbiddenWordsWarning.note')}
                </p>
              </div>
            );
          })()}
        </div>

        {saveError && <p className="admin-form-error">{saveError}</p>}

        {!readOnly && (
          <button type="submit" className="admin-btn-primary" disabled={saving}>
            {saving ? t('common.loading') : t('admin.consentDocs.action.saveDraft')}
          </button>
        )}
      </form>

      {doc && (
        <div className="admin-card admin-consent-docs-actions-card">
          <h3>{t('admin.consentDocs.stateActionsTitle')}</h3>
          <ul className="admin-consent-docs-meta-list">
            <li>{t('admin.consentDocs.col.createdAt')}: {formatDateTimeJst(doc.created_at)}</li>
            <li>{t('admin.consentDocs.col.updatedAt')}: {formatDateTimeJst(doc.updated_at)}</li>
            {doc.published_at && (
              <li>{t('admin.consentDocs.col.publishedAt')}: {formatDateTimeJst(doc.published_at)}</li>
            )}
            {doc.archived_at && (
              <li>{t('admin.consentDocs.col.archivedAt')}: {formatDateTimeJst(doc.archived_at)}</li>
            )}
            {doc.parent_id && (
              <li>
                {t('admin.consentDocs.col.parentId')}:{' '}
                <Link to={`/admin/consent/${doc.parent_id}`}>
                  <code>{doc.parent_id}</code>
                </Link>
              </li>
            )}
          </ul>

          <div className="admin-consent-docs-state-buttons">
            {doc.status === 'draft' && (
              <>
                <button
                  type="button"
                  className="admin-btn-primary"
                  onClick={() => setPublishDialogOpen(true)}
                >
                  {t('admin.consentDocs.action.publish')}
                </button>
                {/* 下書きのみ削除可（課題 No.48）。公開済みは archive を使う */}
                <button
                  type="button"
                  className="admin-btn-danger"
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteDialogOpen(true);
                  }}
                >
                  {t('admin.consentDocs.action.delete')}
                </button>
              </>
            )}
            {doc.status === 'published' && (
              <>
                <button type="button" className="admin-btn-secondary" onClick={handleNewVersion}>
                  {t('admin.consentDocs.action.newVersion')}
                </button>
                <button
                  type="button"
                  className="admin-btn-danger"
                  onClick={() => setArchiveDialogOpen(true)}
                >
                  {t('admin.consentDocs.action.archive')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {publishDialogOpen && doc && (
        <div className="admin-consent-docs-dialog-backdrop" role="dialog" aria-modal="true">
          <div className="admin-consent-docs-dialog">
            <h3>{t('admin.consentDocs.publishDialog.title')}</h3>
            <p>{t('admin.consentDocs.publishDialog.body')}</p>
            <label className="admin-consent-docs-dialog-checkbox">
              <input
                type="checkbox"
                checked={requireReconsent}
                onChange={(e) => setRequireReconsent(e.target.checked)}
              />
              {t('admin.consentDocs.publishDialog.requireReconsent')}
            </label>
            <label className="admin-consent-docs-dialog-checkbox">
              <input
                type="checkbox"
                checked={notifyUsers}
                onChange={(e) => setNotifyUsers(e.target.checked)}
              />
              {t('admin.consentDocs.publishDialog.notifyUsers')}
            </label>
            {requireReconsent && (
              <p className="admin-consent-docs-dialog-warning" role="alert">
                {t('admin.consentDocs.publishDialog.reconsentWarning')}
              </p>
            )}
            {publishError && <p className="admin-form-error">{publishError}</p>}
            <div className="admin-consent-docs-dialog-actions">
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={() => setPublishDialogOpen(false)}
                disabled={publishing}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="admin-btn-primary"
                onClick={handlePublish}
                disabled={publishing}
              >
                {publishing ? t('common.loading') : t('admin.consentDocs.action.publish')}
              </button>
            </div>
          </div>
        </div>
      )}

      {archiveDialogOpen && doc && (
        <div className="admin-consent-docs-dialog-backdrop" role="dialog" aria-modal="true">
          <div className="admin-consent-docs-dialog">
            <h3>{t('admin.consentDocs.archiveDialog.title')}</h3>
            <p>{t('admin.consentDocs.archiveDialog.body')}</p>
            <div className="admin-form-group">
              <label htmlFor="admin-consentDocs-archiveDialog-reasonLabel">{t('admin.consentDocs.archiveDialog.reasonLabel')}</label>
              <input id="admin-consentDocs-archiveDialog-reasonLabel"
                type="text"
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder={t('admin.consentDocs.archiveDialog.reasonPlaceholder')}
              />
            </div>
            {archiveError && <p className="admin-form-error">{archiveError}</p>}
            <div className="admin-consent-docs-dialog-actions">
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={() => setArchiveDialogOpen(false)}
                disabled={archiving}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="admin-btn-danger"
                onClick={handleArchive}
                disabled={archiving}
              >
                {archiving ? t('common.loading') : t('admin.consentDocs.action.archive')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 下書き削除の確認ダイアログ（課題 No.48）。復元できないため既定はキャンセル側 */}
      {deleteDialogOpen && doc && (
        <div className="admin-consent-docs-dialog-backdrop" role="dialog" aria-modal="true">
          <div className="admin-consent-docs-dialog">
            <h3>{t('admin.consentDocs.deleteDialog.title')}</h3>
            <p>{t('admin.consentDocs.deleteDialog.body')}</p>
            <p className="admin-consent-docs-dialog-warning">
              {doc.title}（v{doc.version}）
            </p>
            <p className="admin-form-hint">{t('admin.consentDocs.deleteDialog.note')}</p>
            {deleteError && <p className="admin-form-error">{deleteError}</p>}
            <div className="admin-consent-docs-dialog-actions">
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={deleting}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="admin-btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? t('common.loading') : t('admin.consentDocs.action.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="admin-back">
        <Link to="/admin/consent">
          {t('common.back')} → {t('admin.consentDocs.title')}
        </Link>
      </p>
    </div>
  );
}

function bumpMinor(version: string): string {
  const m = version.match(SEMVER_REGEX);
  if (!m) return version;
  const [major, minor, patch] = version.split('.').map((n) => Number(n));
  if (Number.isNaN(major) || Number.isNaN(minor) || Number.isNaN(patch)) return version;
  return `${major}.${minor + 1}.0`;
}

/**
 * 既存文書のバージョン一覧から、衝突しない次候補（パッチ／マイナー／メジャー）を提案する。
 * 既存がなければ `1.0.0` を返す
 */
function computeVersionSuggestions(
  existing: ConsentDocumentSummary[],
  purpose: string,
  dataType: string
): string[] {
  const sameKey = existing.filter(
    (d) => d.purpose === purpose && d.data_type === dataType && SEMVER_REGEX.test(d.version)
  );
  if (sameKey.length === 0) return ['1.0.0'];
  const parsed = sameKey.map((d) => d.version.split('.').map((n) => Number(n)));
  const max = parsed.reduce((a, b) => {
    if (a[0] !== b[0]) return a[0] > b[0] ? a : b;
    if (a[1] !== b[1]) return a[1] > b[1] ? a : b;
    return a[2] > b[2] ? a : b;
  });
  const [major, minor, patch] = max;
  return [
    `${major}.${minor}.${patch + 1}`,
    `${major}.${minor + 1}.0`,
    `${major + 1}.0.0`,
  ];
}
